import type { MutationFunction, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { BodyType, ErrorType } from '../axios';
import { request } from '../axios';
import type { CreateFileDto, CreateJournalEntryDtoTopic } from '../generated/model';

type RequestOptions = Parameters<typeof request>[1];

export interface CreateJournalEntryRequestDto {
  patientId: string;
  topic: CreateJournalEntryDtoTopic;
  notes: string;
  isTodo: boolean;
  due?: string | null;
  assignedEmployeeId?: string | null;
  file?: CreateFileDto;
}

export const createJournalEntry = (
  createJournalEntryDto: BodyType<CreateJournalEntryRequestDto>,
  options?: RequestOptions,
  signal?: AbortSignal,
) => {
  return request<string>(
    {
      url: `/api/journal`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createJournalEntryDto,
      signal,
    },
    options,
  );
};

export const getCreateJournalEntryMutationOptions = <
  TData = Awaited<ReturnType<typeof createJournalEntry>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { data: BodyType<CreateJournalEntryRequestDto> }, TContext>;
  request?: RequestOptions;
}) => {
  const mutationKey = ['createJournalEntry'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createJournalEntry>>,
    { data: BodyType<CreateJournalEntryRequestDto> }
  > = (props) => {
    const { data } = props ?? {};

    return createJournalEntry(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    { data: BodyType<CreateJournalEntryRequestDto> },
    TContext
  >;
};

export type CreateJournalEntryMutationResult = NonNullable<Awaited<ReturnType<typeof createJournalEntry>>>;
export type CreateJournalEntryMutationBody = BodyType<CreateJournalEntryRequestDto>;
export type CreateJournalEntryMutationError = ErrorType<unknown>;

export const useCreateJournalEntry = <
  TData = Awaited<ReturnType<typeof createJournalEntry>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { data: BodyType<CreateJournalEntryRequestDto> }, TContext>;
  request?: RequestOptions;
}): UseMutationResult<TData, TError, { data: BodyType<CreateJournalEntryRequestDto> }, TContext> => {
  const mutationOptions = getCreateJournalEntryMutationOptions(options);

  return useMutation(mutationOptions);
};

export const updateJournalEntry = (
  id: string,
  createJournalEntryDto: BodyType<CreateJournalEntryRequestDto>,
  options?: RequestOptions,
  signal?: AbortSignal,
) => {
  return request<void>(
    {
      url: `/api/journal/${id}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: createJournalEntryDto,
      signal,
    },
    options,
  );
};

export const getUpdateJournalEntryMutationOptions = <
  TData = Awaited<ReturnType<typeof updateJournalEntry>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { id: string; data: BodyType<CreateJournalEntryRequestDto> }, TContext>;
  request?: RequestOptions;
}) => {
  const mutationKey = ['updateJournalEntry'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateJournalEntry>>,
    { id: string; data: BodyType<CreateJournalEntryRequestDto> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateJournalEntry(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<CreateJournalEntryRequestDto> },
    TContext
  >;
};

export type UpdateJournalEntryMutationResult = NonNullable<Awaited<ReturnType<typeof updateJournalEntry>>>;
export type UpdateJournalEntryMutationBody = BodyType<CreateJournalEntryRequestDto>;
export type UpdateJournalEntryMutationError = ErrorType<unknown>;

export const useUpdateJournalEntry = <
  TData = Awaited<ReturnType<typeof updateJournalEntry>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { id: string; data: BodyType<CreateJournalEntryRequestDto> }, TContext>;
  request?: RequestOptions;
}): UseMutationResult<TData, TError, { id: string; data: BodyType<CreateJournalEntryRequestDto> }, TContext> => {
  const mutationOptions = getUpdateJournalEntryMutationOptions(options);

  return useMutation(mutationOptions);
};
